"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Alert } from "@/components/ui/alert/Alert";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { extractApiErrorMessage } from "@/lib/extractApiErrorMessage";

/** Props for {@link LossReportDialog}. */
export type LossReportDialogProps = {
  /** Whether the dialog is shown */
  open: boolean;
  /** Close the dialog */
  onClose: () => void;
};

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function CountTable({ title, counts, testId }: { title: string; counts: Record<string, number>; testId: string }) {
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <section data-testid={testId}>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">None</p>
      ) : (
        <dl className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map(([label, count]) => (
            <div key={label} className="flex justify-between gap-4 py-1.5 text-sm">
              <dt className="truncate text-slate-700 dark:text-slate-200">{label}</dt>
              <dd className="tabular-nums font-medium text-slate-900 dark:text-slate-100">{count}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** ONB-13 monthly loss report: totals by reason, source, attribution, and adviser. */
export function LossReportDialog({ open, onClose }: LossReportDialogProps) {
  const [period, setPeriod] = useState(currentPeriod);
  const reportQuery = useQuery({
    queryKey: ["onboarding-loss-report", period],
    queryFn: () => AgenticV2API.getLossReport(period, "me"),
    enabled: open && /^\d{4}-\d{2}$/.test(period),
  });
  const report = reportQuery.data;
  const rate = report?.adviser_conversion_rate;

  return (
    <Dialog open={open} onClose={onClose} size="2xl" data-testid="loss-report-dialog">
      <DialogTitle>Loss report</DialogTitle>
      <DialogBody>
        <div className="max-w-xs">
          <FormInput
            id="loss-report-period"
            data-testid="loss-report-period"
            type="month"
            label="Month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          />
        </div>
        {reportQuery.isLoading ? (
          <div className="mt-4 space-y-2">
            <LoadingPlaceholder height="48px" />
            <LoadingPlaceholder height="120px" />
          </div>
        ) : reportQuery.error ? (
          <Alert variant="error" borderless className="mt-4" data-testid="loss-report-error">
            {extractApiErrorMessage(reportQuery.error, "Couldn't load the loss report")}
          </Alert>
        ) : report ? (
          <div className="mt-4 space-y-5">
            <p className="text-sm text-slate-700 dark:text-slate-200" data-testid="loss-report-summary">
              <span className="text-lg font-semibold tabular-nums">{report.total_losses}</span> lost this month
              {rate != null ? ` · adviser conversion ${Math.round(rate * 100)}%` : ""}
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <CountTable title="By reason" counts={report.by_reason} testId="loss-report-by-reason" />
              <CountTable title="Counts against" counts={report.by_attribution} testId="loss-report-by-attribution" />
              <CountTable title="By lead source" counts={report.by_source} testId="loss-report-by-source" />
              <CountTable title="By adviser" counts={report.by_owner} testId="loss-report-by-owner" />
            </div>
          </div>
        ) : null}
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={onClose} data-testid="loss-report-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
